import { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  styled,
  Box,
  Button,
  Container,
  Text,
  Grid,
} from '@classtinginc/design-system';

import { useTranslation } from 'react-i18next';

import ErrorImage from 'assets/images/unknown-error.svg';

const StyledGrid = styled(Grid, {
  gridTemplateAreas: `
    "img img"
    "msg msg"
  `,
  '@sm': {
    gridTemplateAreas: `
      "img img img img img img"
      "msg msg msg msg msg msg"
    `,
  },
  '@lg': {
    gridTemplateAreas: `
      ". . msg msg msg msg msg . img img . ."
    `,
  },
});

const MessageBox = styled(Box, {
  gridArea: 'msg',
  display: 'grid',

  justifyItems: 'center',
  padding: '$layout-03 $00',

  '@lg': {
    padding: '$layout-09 $00',
    justifyItems: 'start',
  },

  h2: {
    fontSize: '$size-140',
    '@lg': {
      fontSize: '$size-160',
    },
  },

  p: {
    fontSize: '$size-60',
    '@lg': {
      fontSize: '$size-80',
    },
  },
});

const ImageBox = styled(Box, {
  display: 'grid',
  gridArea: 'img',
  alignContent: 'center',
  justifyContent: 'center',
  padding: '$layout-01 $00',
  '@lg': {
    padding: '$layout-02 $00',
  },
});

const defaultProps = {
  'data-testid': 'unknown-error-page',
};

type UnknownErrorPageProps = typeof defaultProps;

const UnknownErrorPage = (
  props: UnknownErrorPageProps = defaultProps
): ReactElement => {
  const [t] = useTranslation();

  const history = useHistory();

  const handleGoBack = () => {
    history.go(-1);
  };

  return (
    <Container {...props}>
      <Helmet>
        <title>500 Unknown Error</title>
      </Helmet>
      <StyledGrid>
        <MessageBox>
          <Text
            as="h2"
            size="180"
            color="dark"
            css={{
              marginBottom: '$03',
            }}
          >
            {t('unknown_error_page.title')}
          </Text>
          <Text
            as="p"
            size="90"
            color="light"
            css={{
              marginBottom: '$07',
              whiteSpace: 'pre-line',
            }}
          >
            {t('unknown_error_page.description')}
          </Text>
          <Box>
            <Button
              as="a"
              href="/"
              appearance="outlined"
              color="secondary"
              size="md"
              onClick={handleGoBack}
              css={{
                marginRight: '$02',
              }}
            >
              {t('unknown_error_page.button.go_to_home')}
            </Button>
            <Button
              as="button"
              appearance="contained"
              color="primary"
              size="md"
              onClick={handleGoBack}
              data-testid="go-back"
            >
              {t('unknown_error_page.button.go_back')}
            </Button>
          </Box>
        </MessageBox>
        <ImageBox>
          <ErrorImage />
        </ImageBox>
      </StyledGrid>
    </Container>
  );
};

UnknownErrorPage.displayName = 'UnknownErrorPage';
UnknownErrorPage.defaultProps = defaultProps;

export default UnknownErrorPage;
